import BaseEditorNoEditModules from '@src/slide/application/no-edit/entry/base/editor/base';
import { PluginConfig } from '@src/slide/application/no-edit/service/no-edit-service/no-edit-service.interface';
import {
    IPcEditorExtensionCallBackAdapter,
} from '@src/slide/application/service/edit-extension/core/editor/pc/interface';
import { IReadRecordPlugin } from '@src/slide/application/no-edit/plugins/read-record/read-record-plugin.interface';
import { READ_RECORD_PLUGIN_CONFIG } from '@src/slide/application/no-edit/plugins/read-record/plugin-config';
import { IFileInfoPlugin } from '@src/slide/application/no-edit/plugins/file-info/file-info-plugin.interface';
import { FILE_INFO_PLUGIN_CONFIG } from '@src/slide/application/no-edit/plugins/file-info/plugin-config';
import { ISharePluginPC } from '@src/slide/application/no-edit/plugins/share/base/pc/share-plugin-pc.interface';
import { getUrlParameter } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { SHARE_PC_PLUGIN_NAME } from '@src/slide/application/no-edit/plugins/share/constant';
import { IAuthPluginBase } from '@src/slide/application/no-edit/plugins/auth/base/base/auth-plugin-base.interface';
import { AUTH_PC_PLUGIN_CONFIG_NAME } from '@src/slide/application/no-edit/plugins/auth/constants';
import { AuthEventsKeys } from '@src/slide/application/no-edit/plugins/auth/base/auth-events.interface';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';

export default abstract class NoEditModules extends BaseEditorNoEditModules {
    protected getRegisterPlugins(): PluginConfig[] {
        return [
            this.getSharePcPluginConfig(),
            ...this.getAuthPluginConfig(),
            ...super.getRegisterPlugins(),
        ];
    }

    /**
     * 获取分享plugin，子类可重写，默认为空
     * @protected
     */
    protected getAuthPluginConfig(): PluginConfig[] {
        return [];
    }

    protected getCallbackAdapter(): IPcEditorExtensionCallBackAdapter {
        return {
            ...super.getCallbackAdapter(),
            onAuthClick: async () => {
                const plugin = await this.noEditService.getPlugin<IAuthPluginBase>(AUTH_PC_PLUGIN_CONFIG_NAME);
                plugin?.show({ authUpdateCallback() {} });
            },
            onBrowserReadRecord: async () => {
                const plugin = await this.noEditService.getPlugin<IReadRecordPlugin>(READ_RECORD_PLUGIN_CONFIG.name);
                plugin?.show();
            },
            onDocSizeClick: async () => {
                const plugin = await this.noEditService.getPlugin<IFileInfoPlugin>(FILE_INFO_PLUGIN_CONFIG.name);
                plugin?.showDocSizePanel();
            },
            showShareModule: async () => {
                const plugin = await this.noEditService.getPlugin<ISharePluginPC>(SHARE_PC_PLUGIN_NAME);
                plugin?.show();
            },
        };
    }

    protected onInfrastructureDidReady() {
        super.onInfrastructureDidReady();
        this.handleFileInfoAction();
        this.handleQBConvertToOnlineTips();
        this.initAuthPlugin();
    }

    /**
     * 处理url携带的action，处理打开文档大小面板
     * @private
     */
    private async handleFileInfoAction() {
        const fileInfoPlugin = await this.noEditService.getPlugin<IFileInfoPlugin>(FILE_INFO_PLUGIN_CONFIG.name);
        const action = getUrlParameter('showPanel');
        if (action === 'docSize' && fileInfoPlugin) {
            setTimeout(
                () => {
                    /**
                     * > 文管跳转到编辑页，约定加一个参数 showPanel=docSize
                     * > 形如：https://docs.qq.com/doc/DSFpNWmV5T29TU0Ji?showPanel=docSize
                     *
                     * 参见：https://doc.weixin.qq.com/doc/w3_AP0AfwbdAFwSrrfd9mLQY6RV7Afvd?
                     */
                    fileInfoPlugin.showDocSizePanel();
                },
                // 推迟触发时机，不然依赖的组件都没初始化完
                1000,
            );
        }
    }

    private handleQBConvertToOnlineTips() {
        if (getUrlParameter('from') === 'qb' && getUrlParameter('feature') === 'convertToOnlineDocTips') {
            const folderId = getUrlParameter('folderId');
            if (!folderId) return;
            Snackbar.show({
                type: 'info',
                message: i18n.t('文档已保存在腾讯文档-来自QQ浏览器文件夹'),
                action: i18n.t('点击查看'),
                onClickAction: () => {
                    const url = `https://${location.hostname}/desktop/mydoc/folder/${folderId}`;
                    window.log?.info(`前往 QQ 浏览器文件夹：${url}`);
                    location.href = url;
                },
                duration: 5000,
            });
        }
    }

    /**
     * 初始化Auth plugin
     */
    private initAuthPlugin(): void {
        // 初始化权限plugin
        const authPlugin = this.noEditService.getPluginSync<IAuthPluginBase>(AUTH_PC_PLUGIN_CONFIG_NAME);
        // 监听权限有效期变更
        authPlugin?.onAuthEvent(AuthEventsKeys.AUTH_EXP_TIME_CHANGE, (data) => {
            if (this.docInfoService) {
                this.docInfoService.isAuthExpired = data as boolean;
            }
        });
    }

    protected abstract getSharePcPluginConfig(): PluginConfig;
}
